import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/BillingProvidersService';
import { BillingProvider, BillingProviderStatus } from '../types';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications.tsx';

export const useBillingProvider = (billingProviderId?: string) => {
    const queryClient = useQueryClient();

    // FETCH
    const billingProviderQuery = useQuery({
        queryKey: ['billing-provider', billingProviderId],
        queryFn: () => fetch(billingProviderId!),
        enabled: !!billingProviderId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: BillingProvider) => {
            const valuesWithStatus = {
                ...values,
                status: 'Enabled' as BillingProviderStatus
            };
            return add(valuesWithStatus);
        },
        onSuccess: () => {
            notifySuccess('Billing Provider Added');
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        },
        onError: notifyError
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: BillingProvider) => {
            if (!billingProviderId) throw new Error('billingProviderId is required parameter');
            const { createdByUsername: _createdByUsername, ...rest } = values;
            return update(billingProviderId, rest);
        },
        onSuccess: () => {
            notifySuccess('Billing Provider Updated');
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
            queryClient.invalidateQueries({ queryKey: ['billing-provider', billingProviderId] });
        },
        onError: notifyError
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!billingProviderId) throw new Error('billingProviderId is required parameter');
            return deleteRecord(billingProviderId);
        },
        onSuccess: () => {
            notifySuccess('Billing Provider Deleted');
            queryClient.invalidateQueries({ queryKey: ['billing-providers'] });
        },
        onError: notifyError
    });

    return {
        record: billingProviderQuery.data,
        loading: billingProviderQuery.isLoading,
        fetchRecord: billingProviderQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};
